import React from 'react'
import { Trash2 } from 'lucide-react'
import {
  createAgentOption,
  createPhoneNumberOption,
  createSubmenuOption,
  normalizeIvrConfig,
  updateIvrConfig,
} from './ivrConfiguration'
import ModernTimeDropdown from '../../../../dashboard-components/layouts/modernDropdown'

const fieldStyle = { fontSize: '13px', padding: '6px', width: '100%', borderRadius: '4px', border: '1px solid #e5e7eb' }
const labelStyle = { fontSize: '13px', marginBottom: '4px', display: 'block' }
const phoneNumberInputClassName = 'w-56 px-3 py-2 border border-gray-200 rounded-lg focus:border-indigo-500'
const compactDropdownWidth = 'w-56'
const deleteButtonClassName = 'inline-flex items-center justify-center w-7 h-7 rounded border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
const addButtonClassName = 'inline-flex items-center px-2.5 py-1 text-xs bg-gray-100 text-gray-700 rounded border border-gray-300 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'

const TARGET_TYPE_LABELS = {
  agent: 'Agent',
  submenu: 'Submenu',
  phone_number: 'Phone number',
}

const getTargetTypeOptions = (allowSubmenu) => (
  allowSubmenu
    ? ['Agent', 'Submenu', 'Phone number']
    : ['Agent', 'Phone number']
)

const getTargetForType = (type, digit) => {
  if (type === 'submenu') return createSubmenuOption(digit).target
  if (type === 'phone_number') return createPhoneNumberOption(digit).target
  return createAgentOption(digit).target
}

const sanitizeDigit = (value) => String(value || '').replace(/\D/g, '').slice(0, 1)

const sanitizePhoneNumber = (value) => {
  const input = String(value || '')
  if (input.startsWith('+')) {
    const digits = input.slice(1).replace(/\D/g, '')
    if (digits.length <= 2) return `+${digits}`
    if (!digits.startsWith('91')) return ''
    return `+91${digits.slice(2, 12)}`
  }
  return input.replace(/\D/g, '').slice(0, 10)
}

const ErrorText = ({ message }) => message ? (
  <div style={{ color: '#b91c1c', fontSize: '11px', marginTop: '3px' }}>{message}</div>
) : null

const getError = (errors, path) => errors?.[path]

const AgentSelect = ({ agents, value, onChange }) => {
  const agentOptions = (agents || [])
    .map((agent) => typeof agent === 'string' ? agent : agent.agent_id)
    .filter(Boolean)

  return (
    <ModernTimeDropdown
      width={compactDropdownWidth}
      fontSize="13px"
      value={value || ''}
      placeholder="Select voice agent"
      options={agentOptions}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

const TargetTypeSelect = ({ value, onChange, allowSubmenu = true }) => {
  const options = getTargetTypeOptions(allowSubmenu)
  const selectedLabel = TARGET_TYPE_LABELS[value] || TARGET_TYPE_LABELS.agent

  return (
    <ModernTimeDropdown
      width={compactDropdownWidth}
      fontSize="13px"
      value={selectedLabel}
      placeholder="Select target"
      options={options}
      onChange={(event) => {
        const nextType = Object.keys(TARGET_TYPE_LABELS).find((type) => TARGET_TYPE_LABELS[type] === event.target.value)
        onChange(nextType || 'agent')
      }}
    />
  )
}

const TargetEditor = ({ target, agents, onChange, error }) => {
  if (target?.type === 'phone_number') {
    return (
      <>
        <input
          type="tel"
          value={target.phone_number || ''}
          onChange={(event) => onChange({ type: 'phone_number', phone_number: sanitizePhoneNumber(event.target.value) })}
          className={phoneNumberInputClassName}
          style={{ fontSize: '13px', padding: '0.3rem' }}
          placeholder="Enter phone number"
          maxLength={13}
          inputMode="tel"
          aria-label="Phone number"
        />
        <ErrorText message={error} />
      </>
    )
  }

  if (target?.type === 'agent') {
    return (
      <>
        <AgentSelect
          agents={agents}
          value={target.agent_id}
          onChange={(agentId) => onChange({ type: 'agent', agent_id: agentId })}
        />
        <ErrorText message={error} />
      </>
    )
  }

  return null
}

const SubmenuOptions = ({ option, optionIndex, agents, errors, errorPrefix, onChange }) => {
  const submenu = option.target || {}
  const submenuOptions = Array.isArray(submenu.options) && submenu.options.length
    ? submenu.options
    : [createAgentOption('1')]

  const changeSubmenu = (updater) => onChange((next) => {
    next.options[optionIndex].target = {
      ...next.options[optionIndex].target,
      options: submenuOptions,
    }
    updater(next.options[optionIndex].target)
  })

  return (
    <div style={{ borderLeft: '2px solid #c7d2fe', paddingLeft: '18px', marginLeft: '61px', marginTop: '8px' }}>
      <label style={labelStyle}>Submenu message</label>
      <textarea
        value={submenu.prompt || ''}
        onChange={(event) => changeSubmenu((target) => { target.prompt = event.target.value })}
        style={{ ...fieldStyle, minHeight: '50px' }}
        placeholder="Text the caller will hear before the second digit"
      />
      <ErrorText message={getError(errors, `${errorPrefix}.submenu_prompt`)} />
      {submenuOptions.map((subOption, subIndex) => (
        <div key={`${optionIndex}-${subIndex}`} style={{ marginTop: '7px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '55px 224px 224px auto', gap: '6px', alignItems: 'start', justifyContent: "flex-start" }}>
            <input
              value={subOption.digit || ''}
              type="text"
              inputMode="numeric"
              maxLength={1}
              pattern="[0-9]"
              onChange={(event) => changeSubmenu((target) => { target.options[subIndex].digit = sanitizeDigit(event.target.value) })}
              style={fieldStyle}
              aria-label={`submenu digit ${subIndex + 1}`}
            />
            <TargetTypeSelect
              value={subOption.target?.type || 'agent'}
              allowSubmenu={false}
              onChange={(type) => changeSubmenu((target) => {
                target.options[subIndex].target = getTargetForType(type, subOption.digit || String(subIndex + 1))
              })}
            />
            <TargetEditor
              target={subOption.target}
              agents={agents}
              onChange={(targetValue) => changeSubmenu((target) => { target.options[subIndex].target = targetValue })}
              error={
                getError(errors, `${errorPrefix}.submenu.${subIndex}.phone_number`) ||
                getError(errors, `${errorPrefix}.submenu.${subIndex}.agent_id`)
              }
            />
            <button
              type="button"
              onClick={() => changeSubmenu((target) => { target.options.splice(subIndex, 1) })}
              disabled={submenuOptions.length <= 1}
              className={deleteButtonClassName}
              aria-label={`Remove submenu option ${subIndex + 1}`}
              title="Remove submenu option"
            ><Trash2 size={14} aria-hidden="true" /></button>
          </div>
          <ErrorText message={getError(errors, `${errorPrefix}.submenu.${subIndex}.digit`)} />
        </div>
      ))}
      <button
        type="button"
        onClick={() => changeSubmenu((target) => { target.options.push(createAgentOption(String(target.options.length + 1))) })}
        disabled={submenuOptions.length >= 10}
        className={addButtonClassName}
        style={{ marginTop: '8px' }}
      >Add option</button>
      <ErrorText message={getError(errors, `${errorPrefix}.submenu_options`)} />
    </div>
  )
}

const IvrRoutingConfig = ({ value, agents, errors = {}, onChange }) => {
  const config = normalizeIvrConfig(value)
  const change = (updater) => onChange(updateIvrConfig(config, updater))

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px', marginTop: '8px' }}>
      <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>IVR menu</div>
      <div style={{ fontSize: '12px', color: '#4b5563', marginBottom: '8px' }}>
        The caller hears your text, presses one digit, and is routed to an agent or phone number. A root option can open one submenu.
      </div>
      <label style={labelStyle}>Root menu message</label>
      <textarea
        value={config.prompt || ''}
        onChange={(event) => change((next) => { next.prompt = event.target.value })}
        style={{ ...fieldStyle, minHeight: '55px' }}
        placeholder="For example: Press 1 for Hindi. Press 2 for English."
      />
      <ErrorText message={errors.prompt} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
        <div>
          <label style={labelStyle}>No-input message</label>
          <input value={config.no_input_message || ''} onChange={(event) => change((next) => { next.no_input_message = event.target.value })} style={fieldStyle} />
          <ErrorText message={errors.no_input_message} />
        </div>
        <div>
          <label style={labelStyle}>Invalid-digit message</label>
          <input value={config.invalid_input_message || ''} onChange={(event) => change((next) => { next.invalid_input_message = event.target.value })} style={fieldStyle} />
          <ErrorText message={errors.invalid_input_message} />
        </div>
        <div>
          <label style={labelStyle}>Timeout seconds (5-15)</label>
          <input type="number" min="5" max="15" value={config.timeout_seconds} onChange={(event) => change((next) => { next.timeout_seconds = Number(event.target.value) })} style={fieldStyle} />
          <ErrorText message={errors.timeout_seconds} />
        </div>
        <div>
          <label style={labelStyle}>Invalid attempts (0-3)</label>
          <input type="number" min="0" max="3" value={config.retries} onChange={(event) => change((next) => { next.retries = Number(event.target.value) })} style={fieldStyle} />
          <ErrorText message={errors.retries} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Speed</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="range"
              min="0.6"
              max="1.5"
              step="0.05"
              value={config.speed}
              onChange={(event) => change((next) => { next.speed = Number(event.target.value) })}
              className="w-full"
              aria-label="IVR voice speed"
            />
            <div style={{ width: '60px', textAlign: 'right', fontSize: '13px', color: '#4b5563' }}>
              {Number(config.speed).toFixed(2)}x
            </div>
          </div>
          <ErrorText message={errors.speed} />
        </div>
      </div>
      {config.options.map((option, index) => {
        const errorPrefix = `options.${index}`
        return (
          <div key={index} style={{ borderTop: '1px solid #f3f4f6', paddingTop: '8px', marginTop: '10px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '55px 224px 224px auto', gap: '6px', alignItems: 'start', justifyContent: "flex-start" }}>
              <input value={option.digit || ''} type="text" inputMode="numeric" maxLength={1} pattern="[0-9]" onChange={(event) => change((next) => { next.options[index].digit = sanitizeDigit(event.target.value) })} style={fieldStyle} aria-label={`root digit ${index + 1}`} />
              <TargetTypeSelect
                value={option.target?.type || 'agent'}
                onChange={(type) => change((next) => {
                  next.options[index].target = getTargetForType(type, option.digit || String(index + 1))
                })}
              />
              {option.target?.type === 'agent' || option.target?.type === 'phone_number' ? (
                <TargetEditor
                  target={option.target}
                  agents={agents}
                  onChange={(target) => change((next) => { next.options[index].target = target })}
                  error={option.target.type === 'agent'
                    ? getError(errors, `${errorPrefix}.agent_id`)
                    : getError(errors, `${errorPrefix}.phone_number`)}
                />
              ) : <div />}
              <button
                type="button"
                onClick={() => change((next) => { next.options.splice(index, 1) })}
                disabled={config.options.length <= 1}
                className={deleteButtonClassName}
                aria-label={`Remove root option ${index + 1}`}
                title="Remove root option"
              ><Trash2 size={14} aria-hidden="true" /></button>
              <div style={{ gridColumn: '2 / span 3' }}>
                <ErrorText message={getError(errors, `${errorPrefix}.digit`)} />
              </div>
            </div>
            {option.target?.type === 'submenu' && (
              <SubmenuOptions option={option} optionIndex={index} agents={agents} errors={errors} errorPrefix={errorPrefix} onChange={(updater) => onChange(updateIvrConfig(config, updater))} />
            )}
          </div>
        )
      })}
      <button type="button" onClick={() => change((next) => { next.options.push(createAgentOption(String(next.options.length + 1))) })} disabled={config.options.length >= 10} className={addButtonClassName} style={{ marginTop: '8px' }}>Add option</button>
      <ErrorText message={errors.options} />
    </div>
  )
}

export default IvrRoutingConfig
